export const DREAM_COMPANY_OTHER = "Others";

/** Flat list used for “is listed?” checks */
export const DREAM_COMPANIES = [
  // MAANG
  "Meta", "Apple", "Amazon", "Netflix", "Google",
  // Global Big Tech
  "Microsoft", "Adobe", "Nvidia", "Oracle", "Salesforce", "Uber", "LinkedIn",
  // India IT / MNC
  "TCS", "Infosys", "Wipro", "HCLTech", "Accenture", "Cognizant", "IBM",
  "Capgemini", "Tech Mahindra", "LTIMindtree", "Mphasis", "L&T Technology Services",
  // Consulting & Big 4
  "Deloitte", "KPMG", "PwC", "EY", "BCG", "Bain & Company", "McKinsey",
];

/**
 * Grouped picker — optgroup labels show as sub-headings in the select.
 * MAANG = Meta, Apple, Amazon, Netflix, Google (often said “mango”).
 */
export const DREAM_COMPANY_GROUPS = [
  {
    label: "MAANG",
    companies: ["Meta", "Apple", "Amazon", "Netflix", "Google"],
  },
  {
    label: "Global Big Tech",
    companies: ["Microsoft", "Adobe", "Nvidia", "Oracle", "Salesforce", "Uber", "LinkedIn"],
  },
  {
    label: "India IT / MNC",
    companies: [
      "TCS", "Infosys", "Wipro", "HCLTech", "Accenture", "Cognizant", "IBM",
      "Capgemini", "Tech Mahindra", "LTIMindtree", "Mphasis", "L&T Technology Services",
    ],
  },
  {
    label: "Consulting & Big 4",
    companies: [
      "Deloitte", "KPMG", "PwC", "EY", "BCG", "Bain & Company", "McKinsey",
    ],
  },
];

const TECH_ROLES = [
  "Software Development Engineer (SDE)",
  "SDE – Frontend",
  "SDE – Backend",
  "SDE – Full Stack",
  "Software Engineer – Mobile",
  "Machine Learning Engineer",
  "Data Scientist",
  "Data Engineer",
  "MLOps / AI Engineer",
  "Site Reliability Engineer (SRE)",
  "DevOps Engineer",
  "Cloud Engineer",
  "Security Engineer",
  "Product Manager",
  "Program Manager",
  "UX / Product Designer",
  "Business Analyst",
  "QA / SDET",
];

const INDIA_MNC_ROLES = [
  "Graduate Trainee / GET",
  "Systems Engineer",
  "Software Engineer",
  "Associate Software Engineer",
  "Full Stack Developer",
  "Java Developer",
  "Python Developer",
  ".NET Developer",
  "Data Analyst",
  "Cloud / DevOps Engineer",
  "QA / Test Engineer",
  "Business Analyst",
  "Support Engineer",
  "Consultant – Technology",
];

const CONSULTING_ROLES = [
  "Business Analyst",
  "Associate Consultant",
  "Consultant",
  "Technology Consultant",
  "Strategy Analyst",
  "Digital Transformation Analyst",
  "Data & Analytics Consultant",
  "Risk Advisory Analyst",
  "Audit Associate",
  "Tax Associate",
  "Deal Advisory Analyst",
  "Cyber Risk Analyst",
  "Product Consultant",
  "Implementation Consultant",
];

export const COMPANY_ROLE_BANK = {
  Apple: [
    "Software Engineer", "Hardware Engineer", "Machine Learning Engineer",
    "iOS Engineer", "Product Design Engineer", "Data Scientist",
    "Product Manager", "Technical Program Manager", "Siri / AI Engineer",
  ],
  Microsoft: [
    "Software Engineer", "SDE – Azure", "SDE – Office / M365",
    "Machine Learning Engineer", "Data Scientist",
    "Cloud Solution Architect (Associate)", "Program Manager",
    "Product Manager", "Security Engineer", "Support Engineer / CSS",
  ],
  Google: [
    "Software Engineer (SWE)", "SWE – Frontend", "SWE – Backend",
    "Machine Learning Engineer", "Data Scientist", "Site Reliability Engineer",
    "Product Manager", "Technical Program Manager", "UX Engineer", "Cloud Engineer",
  ],
  Amazon: [
    "Software Development Engineer (SDE)", "SDE – Frontend", "SDE – Backend",
    "Data Engineer", "Machine Learning Engineer", "Business Analyst",
    "Product Manager", "Support Engineer", "QA Engineer",
  ],
  Nvidia: [
    "Software Engineer – CUDA / Systems", "Deep Learning Engineer",
    "Machine Learning Engineer", "GPU Software Engineer", "Research Engineer",
    "Solutions Architect", "Data Scientist", "Firmware Engineer", "Product Manager – AI",
  ],
  Meta: [
    "Software Engineer", "Machine Learning Engineer", "Data Scientist",
    "Data Engineer", "Product Manager", "Research Engineer",
    "iOS / Android Engineer", "Infrastructure Engineer", "UX Researcher / Designer",
  ],
  Adobe: [
    "Software Engineer", "Frontend Engineer", "Machine Learning Engineer",
    "Data Scientist", "Product Manager", "UX Designer", "Cloud Engineer", "Quality Engineer",
  ],
  Netflix: [
    "Software Engineer", "Data Engineer", "Machine Learning Engineer",
    "Content / Analytics Analyst", "Platform Engineer", "Product Manager", "Security Engineer",
  ],
  Oracle: [
    "Software Engineer", "Cloud Engineer – OCI", "Database Administrator / Engineer",
    "Java Developer", "Applications Consultant", "Business Analyst",
    "Support Engineer", "Product Manager",
  ],
  Salesforce: [...TECH_ROLES],
  Uber: [...TECH_ROLES],
  LinkedIn: [...TECH_ROLES],
  TCS: [...INDIA_MNC_ROLES],
  Infosys: [...INDIA_MNC_ROLES],
  Wipro: [...INDIA_MNC_ROLES],
  HCLTech: [...INDIA_MNC_ROLES],
  Accenture: [...INDIA_MNC_ROLES, "Technology Consultant", "Associate Consultant"],
  Cognizant: [...INDIA_MNC_ROLES],
  IBM: [...INDIA_MNC_ROLES, "Cloud Engineer", "AI Engineer"],
  Capgemini: [...INDIA_MNC_ROLES, "Technology Consultant"],
  "Tech Mahindra": [...INDIA_MNC_ROLES],
  LTIMindtree: [...INDIA_MNC_ROLES],
  Mphasis: [...INDIA_MNC_ROLES],
  "L&T Technology Services": [...INDIA_MNC_ROLES],
  McKinsey: [...CONSULTING_ROLES],
  BCG: [...CONSULTING_ROLES],
  "Bain & Company": [...CONSULTING_ROLES],
  Deloitte: [...CONSULTING_ROLES, "SAP Consultant", "Cyber Specialist"],
  KPMG: [...CONSULTING_ROLES],
  PwC: [...CONSULTING_ROLES],
  EY: [...CONSULTING_ROLES],
};

function normalize(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Dice / bigram similarity for fuzzy company matching */
function similarity(a, b) {
  const s1 = normalize(a);
  const s2 = normalize(b);
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;
  if (s1.includes(s2) || s2.includes(s1)) return 0.85;
  const bigrams = (s) => {
    const g = new Set();
    for (let i = 0; i < s.length - 1; i++) g.add(s.slice(i, i + 2));
    return g;
  };
  const A = bigrams(s1);
  const B = bigrams(s2);
  if (!A.size || !B.size) return 0;
  let inter = 0;
  A.forEach((x) => { if (B.has(x)) inter += 1; });
  return (2 * inter) / (A.size + B.size);
}

function isConsultingHint(name) {
  return /consult|advisory|audit|deloitte|kpmg|pwc|ey\b|bain|mckinsey|bcg|accenture|capgemini/i.test(name);
}

function isIndiaMncHint(name) {
  return /tcs|infosys|wipro|hcl|cognizant|ibm|tech mahindra|ltimindtree|mphasis|l.?t technology/i.test(name);
}

export function getRolesForCompany(company) {
  const name = String(company || "").trim();
  if (!name || name === DREAM_COMPANY_OTHER) return [];

  const exact = COMPANY_ROLE_BANK[name];
  if (exact) return [...exact];

  const key = Object.keys(COMPANY_ROLE_BANK).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );
  if (key) return [...COMPANY_ROLE_BANK[key]];

  let best = null;
  let bestScore = 0;
  Object.keys(COMPANY_ROLE_BANK).forEach((k) => {
    const score = similarity(name, k);
    if (score > bestScore) {
      bestScore = score;
      best = k;
    }
  });
  if (best && bestScore >= 0.45) {
    return [...COMPANY_ROLE_BANK[best]];
  }

  if (isConsultingHint(name)) return [...CONSULTING_ROLES];
  if (isIndiaMncHint(name)) return [...INDIA_MNC_ROLES];
  return [...TECH_ROLES];
}

export function isListedDreamCompany(company) {
  const name = String(company || "").trim();
  if (!name) return false;
  return DREAM_COMPANIES.some((c) => c.toLowerCase() === name.toLowerCase());
}

export function companySelectValue(company, othersDraft = false) {
  const name = String(company || "").trim();
  if (!name) return othersDraft ? DREAM_COMPANY_OTHER : "";
  if (name === DREAM_COMPANY_OTHER) return DREAM_COMPANY_OTHER;
  if (isListedDreamCompany(name)) {
    return DREAM_COMPANIES.find((c) => c.toLowerCase() === name.toLowerCase()) || name;
  }
  return name;
}

export function resolveDreamCompany(company) {
  const name = String(company || "").trim();
  if (!name || name === DREAM_COMPANY_OTHER) return "";
  return name;
}

export function roleSelectValue(role, company) {
  const name = String(role || "").trim();
  if (!name) return "";
  const roles = getRolesForCompany(company);
  const hit = roles.find((r) => r.toLowerCase() === name.toLowerCase());
  return hit || "";
}
